import React from "react";

/**
 * Global error boundary. Prevents the "page appears then disappears" symptom by rendering
 * a friendly fallback instead of unmounting the entire tree when a child component throws.
 *
 * On any uncaught render error we:
 *   - log to the console (visible in devtools) for later inspection
 *   - show the user a helpful screen with two recovery actions: reload or clear-and-reload
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[MPY] Uncaught render error:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    // Aggressive recovery: clear localStorage (JWT + client caches) and unregister any Service Worker.
    try { window.localStorage.clear(); } catch (e) { /* ignore */ }
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {})
        .finally(() => window.location.assign("/login"));
    } else {
      window.location.assign("/login");
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || "Erreur inconnue";
    return (
      <div
        className="min-h-screen flex items-center justify-center px-5 bg-[color:var(--seafoam)]"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[color:var(--coral)]/15 flex items-center justify-center mb-4 text-3xl">
            ⚠️
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[color:var(--coral)] mb-1">
            Oups
          </div>
          <h1 className="brand-wordmark text-3xl text-[color:var(--deep)] leading-tight">
            La page a rencontré un pépin
          </h1>
          <p className="text-sm text-slate-500 mt-3">
            Rien de grave — c'est probablement un cache ou une session périmée. Essaie de recharger,
            et si ça persiste, remets-toi à zéro (ça te déconnectera).
          </p>
          <details className="mt-3 text-left text-[11px] text-slate-400 font-mono bg-slate-50 rounded-lg p-2 border border-slate-100">
            <summary className="cursor-pointer">Détails techniques</summary>
            <div className="mt-1 break-words">{msg}</div>
          </details>
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={this.handleReload}
              data-testid="err-reload"
              className="h-11 rounded-full bg-[color:var(--sunshine)] hover:bg-[color:var(--sunshine)]/90 text-[color:var(--deep)] font-bold text-sm transition"
            >
              Recharger la page
            </button>
            <button
              onClick={this.handleReset}
              data-testid="err-reset"
              className="h-11 rounded-full bg-white border-2 border-slate-200 hover:border-[color:var(--coral)] text-slate-700 hover:text-[color:var(--coral)] font-bold text-sm transition"
            >
              Réinitialiser & se reconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }
}
